// @flow
import React, {useState, useEffect} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {Typography, IconButton, Tooltip} from '@material-ui/core';
import {Close, InfoOutlined, Visibility} from '@material-ui/icons';
import Collapsible from '../../../../../elements/Collapsible/Collapsible';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import ConfirmModal from '../../../../../elements/Modals/ConfirmModal';
import CustomModal from '../../../../../elements/CustomModal/CustomModal';
import {colors} from '../../../../../assets/styles/colors';
import ProductVideoEmbed from './ProductVideoEmbed';
import {validateVideoUrl, normalizeVideoInput} from './videoUtils';
import type {ProductVideo} from './videoUtils';

type Props = {
  defaultVideos?: ProductVideo[],
  onChangeVideos?: (videos: ProductVideo[]) => void,
  disabled?: boolean,
};

const useStyles = makeStyles(theme => ({
  sectionTitle: {
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(2),
    },
  },
  productInputField: {
    marginTop: theme.spacing(2),
  },
  collapsibleInput: {
    borderTop: '1px solid #E8E8ED',
    borderBottom: '1px solid #E8E8ED',
    borderRadius: '0!important',
    boxShadow: 'none!important',
    '& .MuiTypography-root': {
      paddingLeft: 0,
      paddingRight: 0,
    },
    '& .MuiCollapse-wrapperInner > div': {
      padding: 0,
    },
  },
  tooltip: {
    marginLeft: theme.spacing(2),
    backgroundColor: '#F4F4F6',
    borderRadius: '8px',
    textAlign: 'center',
    maxWidth: 240,
  },
  textTooltip: {
    textAlign: 'left!important',
    color: ' #717190',
    fontSize: '10px',
    '& span': {
      marginLeft: theme.spacing(1),
    },
  },
  infoBtn: {
    color: '#9595AB',
  },
  videoItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    marginBottom: '8px',
    border: '1px solid #E8E8ED',
    borderRadius: '8px',
    backgroundColor: '#F4F4F6',
  },
  videoText: {
    flex: 1,
    fontSize: '0.875rem',
    wordBreak: 'break-all',
    marginRight: '8px',
  },
  inputContainer: {
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-start',
  },
  videoActions: {
    display: 'flex',
    gap: '4px',
  },
  previewIcon: {
    color: colors.purple3,
  },
  modalContent: {
    padding: '1rem',
    maxWidth: 700,
    width: '100%',
  },
  modalTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: colors.grey900,
  },

  previewVideoContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  previewIframe: {
    width: '100%',
    height: 'auto',
    borderRadius: 'inherit',
    border: 'none',
  },
}));

export default function ProductVideoSection(props: Props) {
  const {defaultVideos = [], onChangeVideos, disabled = false} = props;

  const classes = useStyles();

  const [videos, setVideos] = useState<ProductVideo[]>(defaultVideos);
  const [videoInputValue, setVideoInputValue] = useState('');
  const [isValidUrl, setIsValidUrl] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState<number | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [videoToPreview, setVideoToPreview] = useState<ProductVideo | null>(
    null
  );

  useEffect(() => {
    setVideos(defaultVideos);
  }, [defaultVideos]);

  useEffect(() => {
    if (onChangeVideos) {
      onChangeVideos(videos);
    }
  }, [videos, onChangeVideos]);

  const handleAddVideo = () => {
    if (!videoInputValue.trim()) {
      setIsValidUrl(true);
      setErrorMessage('');
      return;
    }

    const normalizedUrl = normalizeVideoInput(videoInputValue);
    const validation = validateVideoUrl(normalizedUrl);

    if (!validation.isValid) {
      setIsValidUrl(false);
      if (validation.error === 'Plataforma no soportada') {
        setErrorMessage(
          'Plataforma no soportada. Usa YouTube, TikTok, Instagram o Facebook'
        );
      } else if (validation.error === 'No se pudo extraer el ID del video') {
        setErrorMessage(
          'No se pudo procesar la URL. Verifica que sea un enlace válido de video'
        );
      } else {
        setErrorMessage(validation.error || 'Link de video inválido');
      }
      return;
    }

    const newVideo: ProductVideo = {
      url: normalizedUrl,
      socialMediaType: validation.platform,
    };

    setVideos(prev => [...prev, newVideo]);
    setVideoInputValue('');
    setIsValidUrl(true);
    setErrorMessage('');
  };

  const handleRemoveVideoRequest = (videoIndex: number) => {
    setVideoToDelete(videoIndex);
    setConfirmModalOpen(true);
  };

  const handleConfirmRemoveVideo = () => {
    if (videoToDelete !== null) {
      setVideos(prev => prev.filter((_, index) => index !== videoToDelete));
      setVideoToDelete(null);
    }
    setConfirmModalOpen(false);
  };

  const handleCancelRemoveVideo = () => {
    setVideoToDelete(null);
    setConfirmModalOpen(false);
  };

  const handleOpenPreview = (video: ProductVideo) => {
    setVideoToPreview(video);
    setPreviewModalOpen(true);
  };

  const handleClosePreview = () => {
    setVideoToPreview(null);
    setPreviewModalOpen(false);
  };

  const handleVideoInputChange = (value: string) => {
    setVideoInputValue(value);
    if (value === '') {
      setIsValidUrl(true);
      setErrorMessage('');
    }
  };

  return (
    <>
      <Collapsible
        title={
          <>
            <Typography
              className={classes.sectionTitle}
              style={{marginTop: '0', marginRight: '8px'}}
            >
              Links de video (opcional)
            </Typography>
            <Tooltip
              placement="right"
              enterTouchDelay={0}
              classes={{tooltip: classes.tooltip}}
              title={
                <Typography className={classes.textTooltip}>
                  Puedes agregar múltiples videos de diferentes plataformas.
                  <br />
                  <b>Plataformas soportadas:</b>
                  <br />
                  <span>• YouTube (videos, shorts)</span>
                  <br />
                  <span>• TikTok (videos)</span>
                  <br />
                  <span>• Instagram (videos, reels)</span>
                  <br />
                  <span>• Facebook (videos)</span>
                  <br />
                  <br />
                  <b>Cómo agregar videos:</b>
                  <br />
                  <span>
                    <b>Recomendado:</b> Copia el código embed (iframe o
                    blockquote)
                    <br />
                    Esto garantiza que el video se vea con las dimensiones
                    correctas.
                    <br />
                  </span>
                  <span>
                    <b>Alternativa:</b> Copia la URL de la barra de direcciones
                    <br />
                    Puede que el alto o ancho no se vea bien en algunos casos.
                    <br />
                  </span>
                  <br />
                  <b>Nota:</b> Los links cortos de TikTok (vm.tiktok.com) no son
                  soportados. Usa el enlace completo del video desde la app o
                  web.
                </Typography>
              }
            >
              <InfoOutlined color="primary" className={classes.infoBtn} />
            </Tooltip>
          </>
        }
        startClosed={videos.length === 0}
        className={classes.collapsibleInput}
      >
        {videos.length > 0 && (
          <div style={{marginBottom: '16px'}}>
            {videos.map((video, index) => (
              <div key={index} className={classes.videoItem}>
                <Typography className={classes.videoText}>
                  {video.url}
                </Typography>
                <div className={classes.videoActions}>
                  <Tooltip title="Ver preview">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenPreview(video)}
                      disabled={disabled}
                      className={classes.previewIcon}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveVideoRequest(index)}
                    disabled={disabled}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className={classes.inputContainer}>
          <CustomTextField
            id="product-video"
            label="Pega aquí el url para agregar un video"
            autoComplete="off"
            error={!isValidUrl}
            helperText={
              !isValidUrl ? errorMessage || 'Link de video inválido' : ''
            }
            value={videoInputValue}
            type="text"
            disabled={disabled}
            className={classes.productInputField}
            style={{
              marginTop: 0,
              flex: 1,
            }}
            onChange={e => handleVideoInputChange(e.target.value)}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddVideo();
              }
            }}
          />
          <CustomButton
            onClick={handleAddVideo}
            disabled={disabled || !videoInputValue.trim()}
            style={{marginTop: 0, height: '56px'}}
          >
            Agregar
          </CustomButton>
        </div>
      </Collapsible>

      <CustomModal
        open={previewModalOpen}
        handleCloseModal={handleClosePreview}
      >
        <div className={classes.modalContent}>
          <Typography className={classes.modalTitle}>
            Vista previa del video
          </Typography>
          {videoToPreview && (
            <div className={classes.previewVideoContent}>
              <ProductVideoEmbed
                socialMediaType={videoToPreview.socialMediaType}
                url={videoToPreview.url}
                muted
                loop
              />
            </div>
          )}
        </div>
      </CustomModal>

      <ConfirmModal
        open={confirmModalOpen}
        onClose={handleCancelRemoveVideo}
        onCancel={handleCancelRemoveVideo}
        onConfirm={handleConfirmRemoveVideo}
        title="¿Eliminar video?"
        message="¿Estás seguro de que deseas eliminar este video?"
        confirmButtonLabel="Eliminar"
        cancelButtonLabel="Cancelar"
      />
    </>
  );
}
